/**
 * Lets the pre-rendered HTML show the right buttons for a signed-in visitor.
 *
 * Nothing about the session is knowable while Gatsby builds the HTML, so the
 * markup could only ever be the anonymous one: "Sign in" and "Get Started" in
 * the header, "Create my resume" and "Upload resume" in the hero. `AuthProvider`
 * then resolved the session in an effect and those controls changed shape a beat
 * after the page had painted.
 *
 * Reading localStorage during the first render is not the fix -- the build has no
 * storage, so a signed-in visitor's first client render would disagree with the
 * markup, and React 18 answers that by discarding the server tree and rendering
 * the whole page again. That is the double-render this series has been removing.
 *
 * So the session is resolved outside React instead. A synchronous script in
 * `<head>` puts a class on `<html>` before the body is parsed, every variant of
 * the affected controls ships in the HTML, and the stylesheet displays one. React
 * renders all variants on the server and on the client's first render, so the two
 * still agree; the class never enters the React tree.
 *
 * Anonymous is deliberately the *absence* of a class. If the script never runs --
 * JavaScript off, storage blocked in a private window, malformed JSON -- nothing
 * is added and the anonymous variant shows, which is exactly what shipped before.
 * Giving anonymous its own class would instead leave every variant visible.
 */

/** Set when localStorage holds a user with an id but no verified email. */
export const AUTH_CLASS_GUEST = "auth-guest";

/** Set when localStorage holds a user with both an id and an account email. */
export const AUTH_CLASS_VERIFIED = "auth-verified";

/** Every class the script may add, for removal when the session changes. */
export const AUTH_CLASSES = [AUTH_CLASS_GUEST, AUTH_CLASS_VERIFIED];

/**
 * The class for a user object, or `null` for anonymous.
 *
 * Mirrors `isUser` / `isVerified` / `userType` in `~context/AuthContext`:
 * an `id` alone is a guest, an `id` plus an `accountEmail` is verified (the OLD
 * and NEW types both are, and they differ only in id length, which changes no
 * button). `INLINE_SCRIPT` below is the hand-written equivalent of this function;
 * `__test__/lib/authFirstPaint.test.ts` holds the two to the same answers.
 */
export const authClassForUser = (
  user: { id?: string; accountEmail?: string } | null | undefined
): string | null => {
  if (!user || !user.id) return null;
  return user.accountEmail ? AUTH_CLASS_VERIFIED : AUTH_CLASS_GUEST;
};

/**
 * Runs synchronously in `<head>`, so it is deliberately dependency-free: it
 * cannot import `RESUME_USER` or `authClassForUser` because it executes before
 * any bundle. Keep the key and the branches in step with `~lib/localStorage`'s
 * `getLocalUserObject` and with `authClassForUser` above.
 *
 * Swallows everything. A visitor seeing the anonymous buttons for a moment is a
 * blemish; a throw here would be an uncaught error in `<head>` on every page.
 */
export const INLINE_SCRIPT = `
(function(){try{
  var raw=localStorage.getItem('resume_user');
  if(!raw||raw==='undefined')return;
  var u=JSON.parse(raw);
  if(!u||!u.id)return;
  document.documentElement.classList.add(u.accountEmail?'auth-verified':'auth-guest');
}catch(e){}})();`.replace(/\n\s*/g, "");
